// src/utils/offlinePhotoQueue.js
//
// Offline queue for job photo uploads, kept on the phone in IndexedDB.
//
// Why this exists: photos used to rely on the service worker's background
// sync. That replays the original request with the security token from the
// moment the photo was taken, so if the app was reopened or the user logged
// in again before signal came back, the server rejected it and Workbox
// deleted it (same thing that happened to clock-ins on 28/09). On iPhone the
// native app may not run a service worker at all, so nothing caught the
// failed upload. Clock-ins were moved to the app's own queue (App.js
// OfflineSyncManager); this does the same for photos.
//
// IndexedDB rather than localStorage because photos are binary and too big
// for localStorage. Everything survives the app being closed or the phone
// restarting. App.js OfflineSyncManager sends them through axios (fresh
// token every time) when signal returns.

const DB_NAME = 'procleaner-offline';
const DB_VERSION = 1;
const STORE = 'photoUploads';

const openDb = () => new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
        reject(new Error('This device cannot store photos offline.'));
        return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
            db.createObjectStore(STORE, { keyPath: 'id' });
        }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
});

const withStore = async (mode, fn) => {
    const db = await openDb();
    try {
        return await new Promise((resolve, reject) => {
            const tx = db.transaction(STORE, mode);
            const store = tx.objectStore(STORE);
            let result;
            Promise.resolve(fn(store)).then(r => { result = r; }).catch(reject);
            tx.oncomplete = () => resolve(result);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error);
        });
    } finally {
        db.close();
    }
};

const requestToPromise = (req) => new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
});

// ⭐ NEW: Each queued entry is sent later as ONE upload request, so a big pile of
// photos is split into several smaller entries here. Same limits as the online
// upload in StaffJobCard: at most 5 photos and about 20MB per request, which keeps
// every request well under Cloud Run's 32MB limit and the route's photo count.
const MAX_FILES_PER_ENTRY = 5;
const MAX_BYTES_PER_ENTRY = 20 * 1024 * 1024;

const splitIntoEntries = (storedFiles) => {
    const groups = [];
    let current = [];
    let currentBytes = 0;
    storedFiles.forEach(f => {
        const size = f.blob?.size || 0;
        if (current.length > 0 && (current.length >= MAX_FILES_PER_ENTRY || currentBytes + size > MAX_BYTES_PER_ENTRY)) {
            groups.push(current);
            current = [];
            currentBytes = 0;
        }
        current.push(f);
        currentBytes += size;
    });
    if (current.length > 0) groups.push(current);
    return groups;
};

/**
 * Save a batch of photos for one job to upload later.
 * files: array of File or Blob objects (as picked or taken on the job card).
 * Large batches are stored as several smaller entries (see above), all saved
 * together: either every photo is stored or none are.
 * Throws if the phone can't store them, so the caller can keep the previews.
 * Returns the id of the first entry.
 */
export const queuePhotoUpload = async ({ jobId, photoType, instanceDate, timezone, files }) => {
    // Copy each file to a plain Blob with its name and type kept, so it
    // stores reliably on every browser (some can't store File objects).
    const storedFiles = await Promise.all(files.map(async (f, i) => ({
        name: f.name || `photo_${Date.now()}_${i}.jpg`,
        type: f.type || 'image/jpeg',
        blob: new Blob([await f.arrayBuffer()], { type: f.type || 'image/jpeg' }),
    })));

    const takenAt = new Date().toISOString();
    const entries = splitIntoEntries(storedFiles).map((group, i) => ({
        id: `photos_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 9)}`,
        jobId,
        photoType,
        instanceDate: instanceDate || null,
        timezone: timezone || null,
        files: group,
        takenAt,
        attemptCount: 0,
    }));

    // One transaction for all of them
    await withStore('readwrite', (store) => Promise.all(entries.map(entry => requestToPromise(store.put(entry)))));
    return entries[0]?.id || null;
};

/** All queued photo batches, oldest first. Returns [] if storage isn't available. */
export const getQueuedPhotoUploads = async () => {
    try {
        const all = await withStore('readonly', (store) => requestToPromise(store.getAll()));
        return (all || []).sort((a, b) => (a.takenAt || '').localeCompare(b.takenAt || ''));
    } catch (e) {
        console.error('[offlinePhotoQueue] Could not read queued photos:', e);
        return [];
    }
};

export const removeQueuedPhotoUpload = async (id) => {
    try {
        await withStore('readwrite', (store) => requestToPromise(store.delete(id)));
    } catch (e) {
        console.error('[offlinePhotoQueue] Could not remove queued photos:', e);
    }
};

/** Adds one to the attempt count and returns the new count (null on failure). */
export const bumpQueuedPhotoAttempt = async (id) => {
    try {
        // Plain callbacks (no await between the get and the put) so the
        // transaction can't close in between on older Safari.
        return await withStore('readwrite', (store) => new Promise((resolve, reject) => {
            const getReq = store.get(id);
            getReq.onerror = () => reject(getReq.error);
            getReq.onsuccess = () => {
                const entry = getReq.result;
                if (!entry) { resolve(null); return; }
                entry.attemptCount = (entry.attemptCount || 0) + 1;
                const putReq = store.put(entry);
                putReq.onerror = () => reject(putReq.error);
                putReq.onsuccess = () => resolve(entry.attemptCount);
            };
        }));
    } catch (e) {
        console.error('[offlinePhotoQueue] Could not update attempt count:', e);
        return null;
    }
};

/** Rebuilds the exact FormData the online upload sends. */
export const buildPhotoFormData = (entry) => {
    const formData = new FormData();
    formData.append('type', entry.photoType);
    entry.files.forEach(f => {
        formData.append('photos', new File([f.blob], f.name, { type: f.type }));
    });
    if (entry.instanceDate) formData.append('instanceDate', entry.instanceDate);
    if (entry.timezone) formData.append('timezone', entry.timezone);
    return formData;
};